/**
 * Legislation detail store (one per open matter, screenshots/26, 35, 41). Live deltas from the
 * `matter:{recordId}` channel land here; each member row selects only its own slice, so a
 * forecast.update re-renders one row. View state (perspective, client, what-if, expanded, hover)
 * lives here too so keyboard shortcuts, the sticky header and the tabs all see the same state.
 */
import { createStore } from "zustand/vanilla";
import { subscribeWithSelector } from "zustand/middleware";
import { useStore } from "zustand";
import type { ForecastLabel } from "../forecast/forecast";
import type { LegislationDelta, LegislationDetail } from "./types";
import type { MemberSort, Perspective, WhatIf } from "./logic";

export type DetailTab = "overview" | "forecast" | "impact" | "timeline" | "documents" | "lobbying" | "meetings";
export const DETAIL_TABS: DetailTab[] = ["overview", "forecast", "impact", "timeline", "documents", "lobbying", "meetings"];

export interface LegislationState {
  detail: LegislationDetail | null;
  seq: number;
  tab: DetailTab;
  clientId: string | null;
  perspective: Perspective;
  sort: MemberSort;
  whatIfOn: boolean;
  overrides: WhatIf;
  expandedId: string | null;           // member whose "why" is open
  hoverGroup: ForecastLabel | null;    // projection-bar hover → dim the others
  filterGroup: ForecastLabel | null;   // projection-bar click → only that group
  /** Last change per member, for the "+6 pts · reason" tag (cleared by the row after 4s). */
  moved: Record<string, { from: number; at: number; reason: string }>;
  /** Set when a roll call lands while the page is open: rows swap meter → YEA/NAY one by one (L9). */
  revealedAt: number | null;

  hydrate: (d: LegislationDetail, seq: number) => void;
  applyDelta: (seq: number, d: LegislationDelta) => void;
  setTab: (t: DetailTab) => void;
  setClient: (id: string) => void;
  setPerspective: (p: Perspective) => void;
  setSort: (s: MemberSort) => void;
  toggleWhatIf: () => void;
  setOverride: (personId: string, v: "yea" | "nay" | null) => void;
  toggleExpanded: (personId: string) => void;
  setHoverGroup: (g: ForecastLabel | null) => void;
  setFilterGroup: (g: ForecastLabel | null) => void;
  clearMoved: (personId: string) => void;
}

export const createLegislationStore = () => createStore<LegislationState>()(
  subscribeWithSelector((set, get) => ({
    detail: null, seq: 0, tab: "overview", clientId: null, perspective: "their", sort: "persuadable",
    whatIfOn: false, overrides: {}, expandedId: null, hoverGroup: null, filterGroup: null, moved: {}, revealedAt: null,

    hydrate: (detail, seq) => set({
      detail, seq,
      clientId: get().clientId ?? detail.lobbying.clients[0]?.id ?? null,
      perspective: detail.myPosition ? "your" : "their",
    }),

    applyDelta: (seq, d) => {
      const s = get();
      if (!s.detail || seq <= s.seq) return;                 // replayed or stale
      const detail = s.detail;
      switch (d.t) {
        case "forecast.update": {
          const rows = detail.lobbying.influence[d.clientId] ?? [];
          const i = rows.findIndex((r) => r.personId === d.personId);
          if (i < 0) return set({ seq });
          const prev = rows[i];
          const next = rows.slice(); next[i] = { ...prev, inputs: d.inputs, forecast: d.forecast };   // only this row's object changes
          return set({
            seq,
            detail: { ...detail, lobbying: { ...detail.lobbying, influence: { ...detail.lobbying.influence, [d.clientId]: next } } },
            moved: { ...s.moved, [d.personId]: { from: prev.forecast.p, at: Date.now(), reason: d.reason } },
          });
        }
        case "projection.update": return set({ seq, detail: { ...detail, forecast: d.forecast } });
        case "rollcall.recorded": return set({ seq, whatIfOn: false, overrides: {}, revealedAt: Date.now(), detail: { ...detail, rollCall: d.rollCall, backtest: d.backtest, stage: "final" } });
        case "document.add": return set({ seq, detail: { ...detail, documents: [...detail.documents, d.document] } });
        case "timeline.add": return set({ seq, detail: { ...detail, timeline: [d.entry, ...detail.timeline] } });
      }
    },

    setTab: (tab) => set({ tab }),
    setClient: (clientId) => set({ clientId, expandedId: null }),
    setPerspective: (perspective) => set({ perspective }),
    setSort: (sort) => set({ sort }),
    toggleWhatIf: () => set((s) => ({ whatIfOn: !s.whatIfOn, overrides: s.whatIfOn ? {} : s.overrides })),
    setOverride: (id, v) => set((s) => {
      const overrides = { ...s.overrides };
      if (v) overrides[id] = v; else delete overrides[id];
      return { overrides, whatIfOn: true };
    }),
    toggleExpanded: (id) => set((s) => ({ expandedId: s.expandedId === id ? null : id })),
    setHoverGroup: (hoverGroup) => set({ hoverGroup }),
    setFilterGroup: (filterGroup) => set((s) => ({ filterGroup: s.filterGroup === filterGroup ? null : filterGroup })),
    clearMoved: (id) => set((s) => { const moved = { ...s.moved }; delete moved[id]; return { moved }; }),
  })),
);

export type LegislationStore = ReturnType<typeof createLegislationStore>;
export const useLegislation = <U,>(store: LegislationStore, sel: (s: LegislationState) => U) => useStore(store, sel);
